// shared-ui/ReportFrame.tsx · v12.10.14 (REPORT_STANDARD v1.2: footer = "Printed … · Contains patient information · Generated by VoiceSpectrum AI" left, "Page X of Y" right (no patient information: "Printed … · Generated by …"); HTML = "Exported … · Contains patient information" left, "Generated by VoiceSpectrum AI" right; #1621 named-day bar) · 2026-10-12
// shared-ui/ReportFrame.tsx · v12.10.13 (#1577 #1572 #1579 #1580 #1581 #1583 #1594 T78 / #1603 T88 - REPORT_STANDARD v1.0, UI-PROTO-REPORTS-B1 v1.6 LOCKED, UI-PROTO-CALL-RECORD v1.1) · 2026-10-10 · VoiceSpectrum AI
// The frame every report shares (practice app Reports and Support Ops): the FILTERS panel pieces (Dates = quick picks + Custom
// through the shared DateScope control, the Show button with its white dot), the actions menus, the REPORT divider, the print
// header (practice, report · view, PERIOD, RUN, filter line) and footer, and the print / PDF / HTML-export plumbing:
//   * print: the report card is copied into #vs-print-root (everything else on the page is hidden), the page margin boxes are
//     injected for this print only - running header on page 2+ (practice · report · period), footer "Printed <date time> ET" ·
//     "Generated by VoiceSpectrum AI" · "Page X of Y" - and removed after. Ctrl+P on a report screen does the same (beforeprint).
//   * HTML export: one self-contained page (the report's own CSS rules only), header, filter line and footer, no page numbers.
// Light mode only. Counts and words come from the host - this file holds no data.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { DateScopePanel, dateScopeLabel, dateScopeRange, dayName, ymd, type DateScope } from './DateScope';
import './reportFrame.css';

/** What a report was shown for: ISO instants [from, to) on the browser's clock (= the practice's day), plus the words. */
export interface ReportWindow { from: string; to: string; label: string; period: string; days: number; at: number }

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const parseYmd = (s: string) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const md = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`;
const mdy = (d: Date) => `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
/** The longest window a report covers (the API's limit, CallSummaryService.MaxDays). */
export const REPORT_MAX_DAYS = 366;

/** The window of a date choice: "Yesterday · Fri 10/9", "Last 7 days · 10/4 - 10/10", "Oct 2026"; PERIOD 10/09/2026 or a range. */
export function scopeWindow(s: DateScope, now: Date = new Date()): ReportWindow {
  const first = ymd(new Date(now.getFullYear() - 2, 0, 1));   // the control offers 3 years
  const r = dateScopeRange(s, now, first);
  const to = parseYmd(r.to);
  let from = parseYmd(r.from);
  let days = Math.round((to.getTime() - from.getTime()) / 86_400_000) + 1;
  if (days > REPORT_MAX_DAYS) { from = addDays(to, -(REPORT_MAX_DAYS - 1)); days = REPORT_MAX_DAYS; }
  let label: string;
  if (s.kind === 'preset') {
    label = s.value === 'Today' || s.value === 'Yesterday' ? `${s.value} · ${DOW[from.getDay()]} ${md(from)}` : `${s.value} · ${md(from)} - ${md(to)}`;
  } else label = dateScopeLabel(s);
  const period = days === 1 ? mdy(from) : `${mdy(from)} – ${mdy(to)}`;
  return { from: from.toISOString(), to: addDays(to, 1).toISOString(), label, period, days, at: Date.now() };
}

/** A stable key of a date choice (for the Show button's white dot). */
export const scopeKey = (s: DateScope) => JSON.stringify(s);

/** "Oct 10, 2026 11:52 AM ET" - RUN and Printed times are Eastern (REPORT_STANDARD §3). */
export function etTime(d: Date | string = new Date()): string {
  const x = typeof d === 'string' ? new Date(d) : d;
  try {
    const s = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }).format(x);
    return s.replace(/,(\s+\d{1,2}:)/, '$1').replace(/\u202f/g, ' ') + ' ET';
  } catch { return x.toLocaleString() + ' ET'; }
}

const cssStr = (s: string) => s.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/[\r\n]+/g, ' ');

export interface PrintSpec {
  /** The report card (it holds the print header). */
  node: HTMLElement;
  /** Classes the copy is wrapped in so the report's own CSS applies (e.g. "vsr-root cs-root"). */
  wrapClass: string;
  /** Running header for page 2 onward: practice · report · period. */
  running: string;
  /** v12.10.14 (REPORT_STANDARD v1.2): the report carries patient information - the footer says so. */
  phi?: boolean;
}

/** Make the page ready to print the report: returns the clean-up. Idempotent clean-up. */
export function preparePrint(spec: PrintSpec): () => void {
  document.getElementById('vs-print-root')?.remove();
  document.getElementById('vs-print-page')?.remove();
  const root = document.createElement('div');
  root.id = 'vs-print-root';
  root.className = spec.wrapClass;
  root.appendChild(spec.node.cloneNode(true));
  document.body.appendChild(root);
  const st = document.createElement('style');
  st.id = 'vs-print-page';
  const box = 'font:9pt system-ui,-apple-system,"Segoe UI",sans-serif;color:#6b7280';
  st.textContent = `@page{size:letter;margin:16mm 0 16mm;`
    + `@top-left{content:"${cssStr(spec.running)}";${box};padding-left:14mm;vertical-align:bottom}`
    + `@bottom-left{content:"Printed ${cssStr(etTime())}${spec.phi ? ' · Contains patient information' : ''} · Generated by VoiceSpectrum AI";${box};padding-left:14mm;width:160mm;white-space:nowrap}`
    + `@bottom-right{content:"Page " counter(page) " of " counter(pages);${box};padding-right:14mm;white-space:nowrap}}`
    + `@page:first{@top-left{content:none}}`;
  document.head.appendChild(st);
  document.body.classList.add('vsr-printing');
  let done = false;
  return () => {
    if (done) return;
    done = true;
    root.remove(); st.remove(); document.body.classList.remove('vsr-printing');
  };
}

/** Print the report now (Print, and Export › PDF = the print window's "Save as PDF" until #1582). */
export function printReport(spec: PrintSpec) {
  const clean = preparePrint(spec);
  const after = () => { window.removeEventListener('afterprint', after); clean(); };
  window.addEventListener('afterprint', after);
  window.print();
  setTimeout(after, 60_000);
}

/** Ctrl+P on a report screen prints the report, not the app (only while `spec()` gives a report). */
export function useBeforePrint(spec: () => PrintSpec | null) {
  const ref = useRef(spec);
  ref.current = spec;
  useEffect(() => {
    let clean: (() => void) | null = null;
    const before = () => {
      if (document.body.classList.contains('vsr-printing')) return;   // printReport already did it
      const s = ref.current();
      if (s) clean = preparePrint(s);
    };
    const after = () => { clean?.(); clean = null; };
    window.addEventListener('beforeprint', before);
    window.addEventListener('afterprint', after);
    return () => { window.removeEventListener('beforeprint', before); window.removeEventListener('afterprint', after); after(); };
  }, []);
}

/** The CSS rules of this page whose selectors name one of `prefixes` (the report's own look) - for the HTML export. */
function reportCss(prefixes: string[]): string {
  const hit = (sel: string) => prefixes.some((p) => sel.includes(p));
  const walk = (rules: CSSRuleList): string => {
    let out = '';
    for (const r of Array.from(rules)) {
      if (r instanceof CSSStyleRule) { if (hit(r.selectorText) && !r.selectorText.includes('vsr-printing')) out += r.cssText + '\n'; }
      else if (r instanceof CSSMediaRule) { const inner = walk(r.cssRules); if (inner && !/\bprint\b/.test(r.conditionText)) out += `@media ${r.conditionText}{\n${inner}}\n`; }
      else if (typeof CSSSupportsRule !== 'undefined' && r instanceof CSSSupportsRule) { const inner = walk(r.cssRules); if (inner) out += `@supports ${r.conditionText}{\n${inner}}\n`; }
    }
    return out;
  };
  let css = '';
  for (const sh of Array.from(document.styleSheets)) {
    try { css += walk(sh.cssRules); } catch { /* a stylesheet from another origin - not ours */ }
  }
  return css;
}

const SHEET_CSS = `
html,body{margin:0}body{background:#eef0f5;padding:24px 12px;font:14px/1.55 -apple-system,BlinkMacSystemFont,"Segoe UI",Inter,Roboto,system-ui,sans-serif;color:#1a1530}
.vsr-sheet{max-width:880px;margin:0 auto;background:#fff;border:1px solid #e5e7eb;border-radius:6px;box-shadow:0 2px 14px rgba(0,0,0,.07);padding:34px 40px 26px;box-sizing:border-box}
.vsr-sheet .vsr-card{border:0!important;box-shadow:none!important;padding:0!important;margin:0!important}
.vsr-sheet .vsr-ph{display:block!important}.vsr-sheet .vsr-pf{display:flex!important}.vsr-sheet .vsr-noprint{display:none!important}
@media (max-width:700px){body{padding:0}.vsr-sheet{border:0;border-radius:0;box-shadow:none;padding:18px 16px}}
@media print{body{background:#fff;padding:0}.vsr-sheet{border:0;box-shadow:none;padding:0;max-width:none}
 .vsr-sheet *{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
`;

/** Export › HTML (#1594): one self-contained page - header, filter line, footer (no page numbers: it is not paged). */
export function exportHtml(spec: { node: HTMLElement; wrapClass: string; title: string; fileName: string; prefixes: string[] }) {
  const css = reportCss(spec.prefixes) + SHEET_CSS;
  const copy = spec.node.cloneNode(true) as HTMLElement;
  copy.querySelectorAll('.vsr-pf-when').forEach((e) => { e.textContent = `Exported ${etTime()}`; });   // v12.10.14: an export is not printed
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const html = '<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">'
    + `<title>${esc(spec.title)}</title><style>${css}</style></head><body><div class="vsr-sheet"><div class="${spec.wrapClass}">${copy.outerHTML}</div></div></body></html>`;
  const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
  const a = document.createElement('a');
  a.href = url; a.download = spec.fileName; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** A file name part: "Union Medical Group" -> "Union-Medical-Group". */
export const fileSafe = (s: string) => s.replace(/[^A-Za-z0-9 _-]+/g, '').trim().replace(/\s+/g, '-') || 'report';

// ───────────────────────────── the pieces on screen ─────────────────────────────

/** Dates (#1621, DATE-CONTROL v1.2 §2): Today · Yesterday · the three days before by name (Thu · 10/08) · Custom ▾ - Custom opens the
 *  same full locked control as Messages (quick picks included; past only, 3 years, default mode date). */
export function ReportDates({ value, onChange }: { value: DateScope; onChange: (s: DateScope) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const off = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', off); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', off); document.removeEventListener('keydown', esc); };
  }, [open]);
  const t = new Date();
  const day = (n: number) => { const d = new Date(t.getFullYear(), t.getMonth(), t.getDate() - n); return ymd(d); };
  const named = [2, 3, 4].map(day);
  const isPreset = (p: 'Today' | 'Yesterday') => value.kind === 'preset' && value.value === p;
  const isDay = (d: string) => value.kind === 'custom' && value.mode === 'date' && value.from === d;
  const custom = value.kind === 'custom' && !named.some(isDay);
  const pill = (on: boolean, label: string, act: () => void, title?: string) => (
    <button key={label} type="button" className={`vsr-ctl${on ? ' on' : ''}`} aria-pressed={on} title={title} onClick={() => { setOpen(false); act(); }}>{label}</button>
  );
  return (
    <>
      <span className="vsr-lbl">Dates</span>
      <span className="vsr-days" role="group" aria-label="Quick dates">
        {pill(isPreset('Today'), 'Today', () => onChange({ kind: 'preset', value: 'Today' }))}
        {pill(isPreset('Yesterday'), 'Yesterday', () => onChange({ kind: 'preset', value: 'Yesterday' }))}
        {named.map((d) => { const [w, md] = dayName(d).split(' '); return pill(isDay(d), `${w} · ${md}`, () => onChange({ kind: 'custom', mode: 'date', from: d, to: '', year: null, month: null, quarter: null })); })}
      </span>
      <span className="vsr-rel vsr-dsrel" ref={ref}>
        <button type="button" className={`vsr-ctl vsr-dbtn${custom || (value.kind === 'preset' && !isPreset('Today') && !isPreset('Yesterday')) ? ' on' : ''}`} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          <span aria-hidden="true">&#128197;</span> {custom || (value.kind === 'preset' && !isPreset('Today') && !isPreset('Yesterday')) ? dateScopeLabel(value) : 'Custom'} ▾
        </button>
        {open && (
          <DateScopePanel value={value} allow="past" yearsBack={3} defaultMode="date"
            onCancel={() => setOpen(false)} onApply={(s) => { setOpen(false); onChange(s); }} />
        )}
      </span>
    </>
  );
}

/** Show (#1572): nothing loads until it is pressed once; a white dot when the filters changed since. */
export function ShowButton({ dirty, onClick, disabled }: { dirty: boolean; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" className={`vsr-btn pr vsr-show${dirty ? ' dirty' : ''}`} disabled={disabled} onClick={onClick}
      title={dirty ? 'The report is not up to date with the filters - press Show' : undefined}>Show</button>
  );
}

/** A button with a drop-down (Export ▾, Include ▾, Share). `keepOpen`: clicks inside do not close it (forms). */
export function ReportMenu(props: { label: ReactNode; disabled?: boolean; keepOpen?: boolean; align?: 'left' | 'right'; className?: string; children: ReactNode; wide?: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const off = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', off); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', off); document.removeEventListener('keydown', esc); };
  }, [open]);
  useEffect(() => { if (props.disabled) setOpen(false); }, [props.disabled]);
  return (
    <span className="vsr-rel" ref={ref}>
      <button type="button" className={props.className ?? 'vsr-btn'} disabled={props.disabled} aria-expanded={open} onClick={() => setOpen((o) => !o)}>{props.label}</button>
      {open && (
        <div className={`vsr-menu${props.align === 'left' ? ' left' : ''}${props.wide ? ' wide' : ''}`} role="menu"
          onClick={props.keepOpen ? undefined : () => setOpen(false)}>
          {props.children}
        </div>
      )}
    </span>
  );
}

/** The labelled divider above the report card: REPORT · report · dates · practice. */
export function ReportDivider({ label, children }: { label: string; children?: ReactNode }) {
  return <div className="vsr-rhd"><span className="vsr-rk">REPORT</span><span>{label}</span>{children}</div>;
}

/** The print / PDF / HTML header (hidden on screen): practice as the title, report · view, PERIOD and RUN, the filter line. */
export function PrintHeader(props: { practice: string; report: string; view?: string; period: string; run: string; filters?: string[]; note?: string }) {
  const f = (props.filters ?? []).filter(Boolean);
  return (
    <div className="vsr-ph">
      <div className="vsr-ph-r1">
        <div><div className="vsr-ph-t">{props.practice}</div><div className="vsr-ph-s">Reports · {props.report}{props.view && <span> · {props.view}</span>}</div></div>
        <div className="vsr-ph-kv"><div><span className="k">PERIOD</span><b>{props.period}</b></div><div><span className="k">RUN</span><b>{props.run}</b></div></div>
      </div>
      {f.length > 0 && <div className="vsr-ph-flt">{f.map((x) => { const i = x.indexOf(':'); return <span key={x}>{i > 0 ? <>{x.slice(0, i + 1)} <b>{x.slice(i + 1).trim()}</b></> : x}</span>; })}</div>}
      {props.note && <div className="vsr-ph-note">{props.note}</div>}
    </div>
  );
}

/** The footer of the HTML export (print draws its own in the page margins, with page numbers). */
export function PrintFooter({ printed, phi }: { printed: string; phi?: boolean }) {
  return <div className="vsr-pf"><span><span className="vsr-pf-when">Printed {printed}</span>{phi && ' · Contains patient information'}</span><span>Generated by <b>VoiceSpectrum AI</b></span></div>;
}

/** "No calls for …", "Pick the dates and press Show" - never a page of zeros (#1578). */
export function ReportEmpty({ big, children }: { big: string; children?: ReactNode }) {
  return <div className="vsr-empty"><div className="vsr-empty-big">{big}</div>{children}</div>;
}
